import { createContext, useContext, useState, useCallback, useMemo } from "react";
import { getFeaturesForUser } from "../services/UserAuthorization";
import { extractFeaturesFromRoles } from "../utils/extractFeaturesList";

const FeatureContext = createContext(null);
const FEATURES_API_TIMEOUT_MS = 15000;

export const FeatureProvider = ({ children }) => {
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(false);

    const loadFeatures = useCallback(async (userName) => {
        if (!userName) {
            setFeatures([]);
            setFetchError(false);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const res = await Promise.race([
                getFeaturesForUser({ userName }),
                new Promise((_, reject) => {
                    setTimeout(() => reject(new Error("Feature API timeout")), FEATURES_API_TIMEOUT_MS);
                }),
            ]);

            const roles = res?.roles || [];
            setFeatures(extractFeaturesFromRoles(roles));
            setFetchError(false);
        } catch (err) {
            console.error("Failed to load features", err);
            setFeatures([]);
            setFetchError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    const contextValue = useMemo(
        () => ({
            features,
            loading,
            fetchError,
            loadFeatures,
        }),
        [features, loading, fetchError, loadFeatures]
    );

    return (
        <FeatureContext.Provider value={contextValue}>
            {children}
        </FeatureContext.Provider>
    );
};

export const useFeatures = () => {
    const ctx = useContext(FeatureContext);
    if (!ctx) {
        throw new Error("useFeatures must be used inside FeatureProvider");
    }
    return ctx;
};